import { EventInput, EventSourceInput } from '@fullcalendar/core'
import {
  AccountDoc,
  DocType,
  EventDoc,
  EventStatus,
  GroupDoc,
  MasterLabelLabel,
  TaskType
} from 'types/api/changes'
import { Model } from 'types/model'
import { is24meApp } from './appType'
import dayjs from 'dayjs'
import Database from 'services/db/Database'
import { eventLengthInDays, isLateInstance } from './api/instance_state'
import {
  generateGroupcalArrayFromRecurrence,
  mapRecurrency
} from './recurrence'
import { isToday } from 'date-fns'

import {
  isConvertedGroup,
  isConvertedGroupId,
  isOutOfTierGroup,
  isUserCanOperateEvents
} from './api/group_state'
import { highestActiveTier } from './api/accounts'
import { removePrefix } from './object'
import { t } from 'i18next'

interface GroupStatus {
  outOfTierGroup: boolean
  canEdit: boolean
}

export function mapEventsToCalendar(
  events: Model<EventDoc>[] | EventDoc[],
  groups: Model<GroupDoc>[] | GroupDoc[],
  account: Model<AccountDoc> | AccountDoc,
  defaultTz: string,
  guest: boolean,
  somedayDate: string,
  allSomedayDate: string,
  userId: string,
  currentGroup?: string
): EventSourceInput {
  const itemsForUI = buildEventsArrayForUIFromDBEvents(
    events,
    somedayDate,
    allSomedayDate
  )
  const groupStatusMap = new Map<string, GroupStatus>()

  itemsForUI.forEach((event) => {
    const group = groups?.find((group) => group._id === event.GroupID)

    if (group && !groupStatusMap.has(event.GroupID ?? '')) {
      if (group) {
        const outOfTierGroup = isOutOfTierGroup(
          highestActiveTier(account),
          group,
          account,
          userId
        )
        let canEdit = isUserCanOperateEvents(group, userId ?? '')

        groupStatusMap.set(event.GroupID ?? '', { canEdit, outOfTierGroup })
      }
    }
  })

  function isConvertedEventEditable(event: EventDoc) {
    const group = groups?.find((group) => group._id === event.GroupID)
    if (group && isConvertedGroup(group)) {
      return removePrefix(event.GroupID ?? '').includes(event.OwnerID ?? '')
    }
    return false
  }

  const mapped = itemsForUI
    .filter((event) => {
      if (isConvertedGroupId(event.GroupID)) {
        return (
          event.attendees
            ?.find((attendee) => attendee.self)
            ?.responseStatus?.toLowerCase() !== 'declined'
        )
      }

      //we ignore participant status in 24me app
      if (is24meApp()) return true

      const participantStatus =
        event.ParticipantsStatus?.[userId ?? ''] ?? undefined

      return participantStatus?.ParticipantConfirmationStatus != '3'
    })
    .filter((event) => event.Status !== EventStatus.REMOVED)
    .map((event) => {
      const isAllDay = event.AllDay === '1' || event.late
      let startDate = dayjs(Number(event.StartDate) * 1000).tz(defaultTz)

      let endDate = dayjs(Number(event.EndDate) * 1000).tz(defaultTz)

      if (isAllDay) {
        startDate = dayjs.utc(Number(event.StartDate) * 1000)
        endDate = dayjs.utc(Number(event.EndDate) * 1000)

        /**
         * because of mismatch between formats of all day events, we need to add extra day for our data source, in order to show
         * events correctly on a screen. The reason for that is that for example event which goes from October 9 to Octover 10
         * will be saved in our system as "from 2024-10-09 to 2024-10-10", when FullCalendar and some other providers support
         * format as such: "from 2024-10-09 to 2024-10-11", meaning, last day of the long event is day after */

        if (endDate.diff(startDate, 'days') > 0) {
          endDate = endDate.add(1, 'day').startOf('day')
        }

        /**
         * Because of a bug on iOS while adding all day events, they are added in devices timezone midnight as oppose to utc midnight, in order to fix that, we
         * need to add extra 13 hours to cover all timezones, and then make it start of the beginning of a day, we also can check the timezone which
         * will be empty for such events
         */

        if (
          event.TimeZoneNameID?.length === 0 ||
          event.TimeZoneNameID !== 'UTC'
        ) {
          startDate = startDate.add(13, 'hour').startOf('day')
          endDate = endDate.add(13, 'hour').startOf('day')
        }
      }

      let rrule: string | undefined = undefined

      try {
        rrule = mapRecurrency(event, true, false, defaultTz)
      } catch (e) {
        console.error(e)
      }

      if (rrule?.length == 0) {
        rrule = undefined
      }

      const outOfTierGroup = groupStatusMap.get(
        event.GroupID ?? ''
      )?.outOfTierGroup

      let canEdit = isConvertedGroupId(event.GroupID)
        ? isConvertedEventEditable(event)
        : groupStatusMap.get(event.GroupID ?? '')?.canEdit

      const minutes = endDate.diff(startDate, 'minutes')

      const eventLen = eventLengthInDays(event) + 1

      const data = {
        end: endDate.format(isAllDay ? 'YYYY-MM-DD' : 'YYYY-MM-DD HH:mm:ss'),
        id: `${event.late}${event._id}`,
        title: event.Text,
        start: startDate.format(
          isAllDay ? 'YYYY-MM-DD' : 'YYYY-MM-DD HH:mm:ss'
        ),
        allDay: isAllDay || event.late,
        durationEditable:
          is24meApp() ||
          (!guest &&
            (event._id?.length ?? 0) > 0 &&
            event.someday != '1' &&
            canEdit &&
            !outOfTierGroup &&
            (event.GroupID === currentGroup || !currentGroup)),
        editable:
          is24meApp() ||
          (!guest &&
            canEdit &&
            !outOfTierGroup &&
            (event.GroupID === currentGroup || !currentGroup)),
        eventModel: event,
        rrule: event.late ? undefined : rrule,
        duration: {
          days: rrule && isAllDay ? eventLen : undefined,
          minutes: rrule && isAllDay ? undefined : minutes > 0 ? minutes : 20
        }
      }

      return data
    })

  return mapped
}

export function buildEventsArrayForUIFromDBEvents(
  events: Model<EventDoc>[] | EventDoc[],
  somedayDate: string,
  allSomedayDate: string
) {
  let itemsForUI = is24meApp()
    ? prepareTasksForUI(events, somedayDate, allSomedayDate)
    : events

  if (is24meApp()) {
    itemsForUI = [
      ...itemsForUI,
      ...generateLateTasksForEvents(
        itemsForUI.filter(
          (event) => isLateInstance(event, true) && (event._id?.length ?? 0 > 0)
        )
      ),
      ...generateLateRecurrentInstances(itemsForUI)
    ]
  }

  return itemsForUI
}

export function generateLateRecurrentInstances(
  arg0: Model<EventDoc>[] | EventDoc[]
) {
  const relevantItems = arg0.filter(
    (event) =>
      event.Type === DocType.TASK &&
      event.Recurrence &&
      typeof event.Recurrence !== 'string' &&
      isLateInstance(event) &&
      (event._id?.length ?? 0 > 0)
  )

  const lateInstances: any = []

  // convert this map to a for loop

  for (let i = 0; i < relevantItems.length; i++) {
    const event = relevantItems[i]
    const parsedItems = generateGroupcalArrayFromRecurrence(event, true)

    if (parsedItems.some((event) => isToday(Number(event.StartDate) * 1000)))
      continue

    const filteredItems = parsedItems.filter((item) => isLateInstance(item))
    const reversedItems = filteredItems.reverse()
    const uniqueItems = reversedItems.filter(
      (item: EventDoc, index: number, self: EventDoc[]) =>
        index === self.findIndex((event: EventDoc) => event._id === item._id)
    )
    const instance = uniqueItems[0]
    if (
      instance &&
      (instance.Status === EventStatus.ACTIVE ||
        instance.Status === EventStatus.COMPLETED)
    )
      lateInstances.push(createLateFromEvent(instance))
    else continue
  }
  return lateInstances
}

export function createLateFromEvent(event: EventDoc): EventDoc {
  const now = dayjs().tz('UTC')

  const eventStart = dayjs(Number(event.StartDate) * 1000)
  const eventEnd = dayjs(Number(event.EndDate) * 1000)
  const duration = eventEnd.diff(eventStart)
  const lateStart = now.startOf('day')

  return {
    ...event,
    ...Database.toServerObject(event),
    StartDate: lateStart.unix().toString(),
    EndDate: lateStart.add(1, 'second').unix().toString(),
    TimeZoneNameID: event.TimeZoneNameID,
    late: true,
    lateOriginalStartTime: event.StartDate,
    lateOriginalEndTime: event.EndDate
  }
}

export function generateLateTasksForEvents(events: EventDoc[]): EventDoc[] {
  const late = events
    .filter((event) => isLateInstance(event) && event.Type === DocType.TASK)
    .map((event) => {
      return createLateFromEvent(event)
    })

  return late
}

export function prepareTasksForUI(
  events: EventDoc[] | undefined,
  somedayDate: string,
  allSomedayDate: string,
  masterLabelLabel?: MasterLabelLabel,
  showingDocType?: DocType
): EventDoc[] {
  if (!events) return []
  const filtered = events
    .filter((event) => {
      return event.TaskType != TaskType.birthday
    })
    .filter((event) => {
      if (!masterLabelLabel) return true // return all items

      if (event.Label && typeof event.Label !== 'string') {
        return event.Label.some(
          (ele) =>
            ele.LabelText === masterLabelLabel?.ID &&
            event.Type === showingDocType
        )
      }
    })

  const compositeSomedayTask = makeCompositeSomedayTask(
    filtered,
    somedayDate,
    allSomedayDate
  )

  return compositeSomedayTask
    ? [
        ...filtered.filter((event) => {
          return event.someday != '1'
        }),
        compositeSomedayTask
      ]
    : filtered
}

export function makeCompositeSomedayTask(
  events: EventDoc[],
  somedayDate: string,
  allSomedayDate: string
) {
  const somedayTask = events.filter((event) => {
    return (
      event.someday === '1' ||
      (event.StartDate === 'null' && event.EndDate === 'null')
    )
  })

  let compositeSomedayTask: Model<EventDoc> | EventDoc | undefined = undefined

  if (somedayTask && somedayTask.length > 0 && is24meApp()) {
    let start = dayjs().tz('UTC').startOf('day')
    let end = start.add(1, 'second')

    if (somedayDate) {
      start = dayjs.unix(Number(somedayDate))
      end = start.add(1, 'second')
    }

    compositeSomedayTask = {
      ...somedayTask[0],
      ...Database.toServerObject(somedayTask[0]),
      Text: t('somedayTask', { amount: somedayTask.length }) ?? '',
      Label: undefined,
      someday: '1',
      AllDay: allSomedayDate ? (allSomedayDate ?? '0') : '1',
      Status: EventStatus.ACTIVE,
      Priority: '0',
      StartDate: start.unix().toString(),
      EndDate: end.unix().toString(),
      TaskType: TaskType.meet,
      Type: DocType.TASK,
      _id: `s${somedayTask[0]._id}`
    }
  }
  return compositeSomedayTask
}
